// ═══════════════════════════════════════════════════════════════════════════════
// Contraseñas y errores de Auth (Lote B2 de la auditoría, nov-2026).
//
// La política la impone Supabase Auth (Authentication → Password): mínimo 10
// caracteres, con letras y dígitos, y rechazo de contraseñas filtradas (HIBP).
// Esta validación local solo adelanta el aviso para que el usuario sepa qué le
// falta antes de enviar; lo que manda es lo que responde Auth.
// Si se cambia la política en el panel, cambiar CLAVE_MIN aquí también.
// ═══════════════════════════════════════════════════════════════════════════════

export const CLAVE_MIN = 10;

export const REGLA_CLAVE = `Mínimo ${CLAVE_MIN} caracteres, con letras y números.`;

// Devuelve '' si la contraseña cumple, o un mensaje que dice exactamente qué falta.
export function validarClave(p) {
  const clave = String(p || '');
  const falta = [];
  if (clave.length < CLAVE_MIN) falta.push(`al menos ${CLAVE_MIN} caracteres (llevas ${clave.length})`);
  if (!/\p{L}/u.test(clave))    falta.push('al menos una letra');
  if (!/\d/.test(clave))        falta.push('al menos un número');
  if (!falta.length) return '';
  return `A tu contraseña le falta: ${unir(falta)}.`;
}

function unir(xs) {
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;
}

export const MSG_SESION_VENCIDA =
  'Tu sesión venció. Vuelve a entrar con tu correo y tu contraseña.';

const CODIGOS_SESION = new Set([
  'session_expired', 'session_not_found', 'refresh_token_not_found',
  'refresh_token_already_used', 'bad_jwt', 'no_authorization',
]);

// Traduce un error de Supabase Auth a un mensaje en español (tuteo).
// `respaldo` es el mensaje genérico de la pantalla si el error no es conocido.
export function traducirErrorAuth(error, respaldo = 'No se pudo completar la operación. Intenta de nuevo.') {
  if (!error) return '';
  const code    = error.code || error.error_code || '';
  const reasons = error.reasons || error.weak_password?.reasons || [];
  const msg     = String(error.message || '').toLowerCase();

  if (code === 'weak_password' || msg.includes('password should') || msg.includes('weak password')) {
    if (reasons.includes('pwned') || msg.includes('pwned') || msg.includes('leaked') || msg.includes('compromised')) {
      return 'Esa contraseña aparece en filtraciones públicas de datos y no es segura. Elige otra que no uses en ningún otro sitio.';
    }
    const falta = [];
    if (reasons.includes('length') || msg.includes('at least')) falta.push(`al menos ${CLAVE_MIN} caracteres`);
    if (reasons.includes('characters') || msg.includes('character')) falta.push('letras y números');
    return falta.length
      ? `La contraseña no cumple la política: necesita ${unir(falta)}.`
      : `La contraseña no cumple la política. ${REGLA_CLAVE}`;
  }
  // Verificación en dos pasos (TOTP).
  if (code === 'mfa_verification_failed' || msg.includes('invalid totp')) {
    return 'El código no es correcto o ya venció. Escribe el que muestra ahora tu app.';
  }
  if (code === 'mfa_challenge_expired') return 'El código tardó demasiado. Escribe el que muestra ahora tu app.';
  if (code === 'insufficient_aal')      return 'Esta acción pide tu código de verificación en dos pasos. Sal y vuelve a entrar.';
  if (code === 'mfa_factor_name_conflict' || code === 'too_many_enrolled_mfa_factors') {
    return 'Ya hay una verificación en dos pasos a medias. Cierra esta ventana y vuelve a intentarlo.';
  }
  if (code === 'same_password') return 'La contraseña nueva tiene que ser distinta de la actual.';
  const sesionMsg = msg.includes('session') && (msg.includes('expired') || msg.includes('missing') || msg.includes('not found'));
  if (CODIGOS_SESION.has(code) || sesionMsg) {
    return MSG_SESION_VENCIDA;
  }
  if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit' || error.status === 429) {
    return 'Hiciste demasiados intentos seguidos. Espera unos minutos y vuelve a intentar.';
  }
  return respaldo;
}
